import { useState } from "react";

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

const FancyBookModal = ({ isOpen, onClose, fancyId, beventId, nation, bookData, isLoading }) => {
  const [activeTab, setActiveTab] = useState('Book');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[70vh] overflow-hidden relative z-10 flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-[#0c1524] to-[#162032] px-5 py-3.5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-1 h-5 bg-amber-400 rounded-full" />
            <span className="text-white font-semibold text-sm tracking-wide">
              {nation || bookData?.data?.summary?.fancyName || 'Fancy Book'}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-white px-3 gap-1 shrink-0">
          {['Book', 'Bets'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-xs font-semibold transition-all border-b-2 ${
                activeTab === tab
                  ? 'text-teal-600 border-teal-500'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-teal-500 rounded-full animate-spin" />
              <p className="text-slate-400 text-xs font-medium">Loading...</p>
            </div>
          ) : activeTab === 'Book' ? (
            <div className="overflow-x-auto">
              {bookData?.data?.book?.length > 0 ? (
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#0F2327] text-white">
                      <th className="px-4 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap border-b border-[#1a3a40]">Runs</th>
                      <th className="px-4 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap border-b border-[#1a3a40]">P&amp;L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookData.data.book.map((item, index) => {
                      const pl = typeof item.profitLoss === 'number' ? item.profitLoss : parseFloat(item.profitLoss) || 0;
                      return (
                        <tr key={index} className={`border-b border-slate-100 hover:brightness-95 ${index % 2 !== 0 ? 'bg-slate-50/50' : 'bg-white'}`}>
                          <td className="px-4 py-2.5 text-slate-700">{typeof item.runs === 'number' ? item.runs.toFixed(2) : item.runs}</td>
                          <td className={`px-4 py-2.5 font-semibold ${pl < 0 ? 'text-red-500' : 'text-emerald-600'}`}>{pl.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <p className="text-xs font-medium">{bookData?.message || 'No book data available'}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bets-table overflow-x-auto">
              {bookData?.data?.bets?.length > 0 ? (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#0F2327] text-white">
                      {['Rate', 'Volume', 'Amount', 'Type', 'Team', 'Client', 'Agent', 'Date', 'Loss', 'Profit'].map(h => (
                        <th key={h} className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap border-b border-[#1a3a40]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bookData.data.bets.map((bet, index) => {
                      const isBack = bet.betType === 'back';
                      return (
                        <tr
                          key={index}
                          className={`border-b border-slate-100 ${isBack ? 'bet-row-back' : 'bet-row-lay'}`}
                        >
                          <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{bet.runs ?? '-'}</td>
                          <td className="px-2 py-2 text-slate-600 whitespace-nowrap">{bet.size ?? '-'}</td>
                          <td className="px-2 py-2 font-semibold text-slate-800 whitespace-nowrap">{bet.stake ?? '-'}</td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${isBack ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                              {isBack ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{nation || '-'}</td>
                          <td className="px-2 py-2 text-slate-700 whitespace-nowrap">
                            <div className="font-bold text-slate-900">{bet.fullName || bet.username || '-'}</div>
                            {bet.fullName && bet.username && <div className="text-[11px] text-blue-500 font-semibold">{bet.username}</div>}
                          </td>
                          <td className="px-2 py-2 text-slate-600 whitespace-nowrap">{bet.agentName || bet.agentUserId || '-'}</td>
                          <td className="px-2 py-2 text-slate-500 whitespace-nowrap">{formatDate(bet.placedAt)}</td>
                          <td className="px-2 py-2 font-semibold text-red-500 whitespace-nowrap">{bet.potentialLoss != null ? bet.potentialLoss.toFixed(2) : '-'}</td>
                          <td className="px-2 py-2 font-semibold text-emerald-600 whitespace-nowrap">{bet.potentialWin != null ? bet.potentialWin.toFixed(2) : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <p className="text-xs font-medium">No bets data available</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-5 py-3 flex justify-end border-t border-slate-200 shrink-0">
          <button
            onClick={onClose}
            className="bg-[#0c1524] hover:bg-[#162032] text-white px-6 py-1.5 rounded-lg text-xs font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FancyBookModal;
